import React, { useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import TextInput from '@/components/Typography/TextInput';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import useMacFontScale from '@/hooks/useMacFontScale';
import { macGoalAppearance } from './workGoalAppearance';

export default function WorkGoalEditor({ initialObjective = '', onSubmit, onCancel, disabled = false, editing = false, embedded = false }) {
  const { colors, theme } = useTheme();
  const isMac = Platform.OS === 'macos';
  const palette = isMac ? macGoalAppearance(theme) : null;
  const fontScale = useMacFontScale();
  const [objective, setObjective] = useState(initialObjective);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);
  const [footerWidth, setFooterWidth] = useState(0);
  const stackedFooter = isMac && footerWidth > 0 && footerWidth < 520 * fontScale;
  const saving = useRef(false);
  const locked = busy || disabled;
  const save = async () => {
    if (locked || saving.current || !objective.trim()) return;
    saving.current = true;
    setBusy(true); setError('');
    try {
      const accepted = await onSubmit(objective.trim());
      if (accepted === false) setError('The goal has not started. Resolve the request, then try again.');
    } catch (err) { setError(err?.message || 'Could not start the goal. Please try again.'); }
    finally { saving.current = false; setBusy(false); }
  };
  return (
    <ScrollView keyboardShouldPersistTaps="always" scrollEnabled={false}
      contentContainerStyle={[styles.content, isMac && macStyles.content]}
      style={[styles.container, isMac && macStyles.container, isMac && embedded && macStyles.embedded, {
        backgroundColor: palette?.surface || colors.solid_color_primary, borderColor: palette?.border || colors.border_color_secondary,
      }]}>
      <View style={isMac && macStyles.header}>
        {isMac && <View style={[macStyles.symbol, { backgroundColor: palette.field }]}>
          <Icon name="bullseye" size={18} color={palette.text} />
        </View>}
        <View style={styles.heading}>
          <Text size="s" weight="semibold" style={isMac && [macStyles.title, { color: palette.text }]}>
            {editing ? 'Edit goal' : 'Set a goal'}
          </Text>
          <Text size="xs" color="secondary" style={isMac && [macStyles.subtitle, { color: palette.secondary }]}>
            What should Work keep working toward?
          </Text>
        </View>
      </View>
      <View style={isMac && [macStyles.field, {
        backgroundColor: palette.field, borderColor: focused ? palette.focus : palette.border,
      }]}>
        <TextInput accessibilityLabel="Goal objective" autoFocus value={objective} onChangeText={setObjective}
          multiline maxLength={8000} editable={!locked}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          {...(isMac ? { enableFocusRing: false, submitBehavior: 'newline', submitKeyEvents: [] } : {})}
          placeholder="Describe the outcome and how to verify it."
          placeholderTextColor={palette?.secondary || colors.text_color_tertiary}
          style={[styles.input, { color: palette?.text || colors.text_color_primary, borderColor: colors.border_color_secondary }, isMac && macStyles.input]}
          onKeyPress={event => {
            if (event.isComposing || event.nativeEvent?.isComposing) return;
            const key = event.key || event.nativeEvent?.key;
            if (key === 'Escape' && !locked) { event.preventDefault?.(); onCancel(); }
            // React Native Web owns onKeyDown and forwards it to onKeyPress.
            if (Platform.OS === 'web' && key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault(); void save();
            }
          }}
        />
      </View>
      <View onLayout={isMac ? event => setFooterWidth(Math.round(event.nativeEvent.layout.width)) : undefined}
        style={isMac && [macStyles.footer, stackedFooter && macStyles.stackedFooter]}>
        <Text size="xs" color="secondary" style={isMac && [macStyles.hint, stackedFooter && macStyles.stackedHint, { color: palette.secondary }]}>
          {isMac ? 'Work keeps going until it’s done or needs your input. You can pause anytime.'
            : 'Work continues across replies until the goal is complete or needs your attention. You can pause anytime.'}
        </Text>
        <View style={[styles.actions, isMac && macStyles.actions]}>
          <Pressable accessibilityRole="button" accessibilityLabel="Cancel goal editing" disabled={locked}
            accessibilityState={{ disabled: locked }}
            onPress={onCancel} style={({ hovered, pressed }) => [styles.button, isMac && macStyles.button,
              isMac && (hovered || pressed) && { backgroundColor: palette.hover }, locked && styles.disabled]}>
            <Text size="s" color="secondary" style={isMac && [macStyles.buttonText, { color: palette.secondary }]}>Cancel</Text>
          </Pressable>
          <Pressable accessibilityRole="button" accessibilityLabel={editing ? 'Save and start' : 'Start goal'}
            disabled={locked || !objective.trim()} accessibilityState={{ disabled: locked || !objective.trim(), busy }}
            onPress={save} style={({ hovered, pressed }) => [styles.button, isMac && macStyles.button,
              { backgroundColor: palette?.action || colors.solid_color_secondary },
              isMac && (hovered || pressed) && { opacity: 0.85 }, (locked || !objective.trim()) && styles.disabled]}>
            <Text size="s" weight="medium" style={isMac && [macStyles.buttonText, { color: palette.actionText }]}>
              {busy ? 'Starting…' : editing ? 'Save and start' : 'Start goal'}
            </Text>
          </Pressable>
        </View>
      </View>
      {!!error && <Text size="xs" accessibilityRole="alert">{error}</Text>}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { marginHorizontal: 8, marginBottom: 8, borderWidth: 1, borderRadius: 14, flexGrow: 0, flexShrink: 0 },
  content: { padding: 14 },
  heading: { flexShrink: 1, gap: 3 },
  input: { minHeight: 90, maxHeight: 160, marginVertical: 10, borderWidth: 1, borderRadius: 10, padding: 10, fontSize: 15, textAlignVertical: 'top' },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 10 },
  button: { minHeight: 40, paddingHorizontal: 14, borderRadius: 8, justifyContent: 'center' },
  disabled: { opacity: 0.45 },
});

const macStyles = StyleSheet.create({
  container: { marginHorizontal: 0, marginBottom: 10, borderRadius: 16 },
  embedded: { borderWidth: 0, borderRadius: 0, marginBottom: 0 },
  content: { padding: 20, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  symbol: { width: 38, height: 38, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  title: { fontFamily: 'System', fontSize: 16, lineHeight: 21, fontWeight: '600' },
  subtitle: { fontFamily: 'System', fontSize: 12, lineHeight: 17 },
  field: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10 },
  input: { fontFamily: 'System', fontSize: 14, lineHeight: 21, minHeight: 76, maxHeight: 168,
    borderWidth: 0, borderRadius: 0, padding: 0, marginVertical: 0 },
  footer: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  stackedFooter: { flexDirection: 'column', alignItems: 'stretch' },
  actions: { marginTop: 0, marginLeft: 'auto', flexShrink: 0 },
  hint: { fontFamily: 'System', fontSize: 12, lineHeight: 17, flexBasis: 0, flexGrow: 1, flexShrink: 1 },
  stackedHint: { flexBasis: 'auto', flexGrow: 0, flexShrink: 0 },
  button: { minHeight: 34, paddingVertical: 7, paddingHorizontal: 14, borderRadius: 8 },
  buttonText: { fontFamily: 'System', fontSize: 13, lineHeight: 18 },
});
